import { Injectable } from '@angular/core';
import { MessagingGroupModel } from '../api/models/messaging-group-model';
import { MessageModel } from '../api/models/message-model';
import { BehaviorSubject, lastValueFrom, Subject, Subscription } from 'rxjs';
import { EventsService } from '../events/events.service';
import { NotificationModel } from '../api/models/notification-model';
import { LocalNotificationsService } from '../core/services/local-notifications.service';
import { MessagingService as ApiMessagingService } from '../api/services/messaging.service';
import { GridSortModel } from '../api/models/grid-sort-model';
import { SendMessageModel } from '../api/models/send-message-model';
import { BillingCycleLimitModel } from '../api/models/billing-cycle-limit-model';
import { SmsPhoneNumberModel } from '../api/models/sms-phone-number-model';

@Injectable()
export class MessagingService {
  getNumbersInProgress = true;
  messagingNumbers: SmsPhoneNumberModel[] = [];
  selectedMessagingNumber: SmsPhoneNumberModel = { phoneNumber: '' };
  totalGroups = 0;
  messagingGroups = new BehaviorSubject<MessagingGroupModel[]>([]);
  onNewMessageSubject = new BehaviorSubject<MessageModel>(null);
  onDeletedMessageSubject = new BehaviorSubject<MessageModel | undefined>(undefined);
  onWebSocketConnectSubject = new Subject<void>();
  onNewNotificationsSubject = new BehaviorSubject<NotificationModel[]>([]);
  notificationsSubscription: Subscription;

  isComposing = false;

  constructor(
    private eventService: EventsService,
    private notificationsService: LocalNotificationsService,
    private apiMessagingService: ApiMessagingService,
  ) {
    this.eventService.subscribeEvents('sms-deleted', (event, data: MessageModel) => {
      this.onDeletedMessageSubject.next(data);
    });

    this.eventService.subscribeEvents('sms-in', (event, data: MessageModel) => {
      this.onNewMessageSubject.next(data);
    });

    this.eventService.subscribeEvents('sms-out', (event, data: MessageModel) => {
      this.onNewMessageSubject.next(data);
    });

    this.eventService.subscribeEvents('connect', (event, data: MessageModel) => {
      this.onWebSocketConnectSubject.next();
    });

    this.notificationsSubscription = this.notificationsService.latestNotifications.subscribe(data => {
      this.onNewNotificationsSubject.next(data);
      this.updatePhoneNumbersBadges(data);
    });
  }

  getMessagingNumber(): string {
    return this.selectedMessagingNumber ? this.selectedMessagingNumber.phoneNumber : '';
  }

  hasMessagingNumber() {
    return this.messagingNumbers.length > 0;
  }

  public async queryPhoneNumberThreads(options: {
    phoneNumber: string;
    skip?: number;
    limit?: number;
    searchText?: string;
    sort?: Array<GridSortModel>;
    threadKey?: string;
    forceUpdate: boolean;
  }): Promise<MessagingGroupModel[]> {
    console.log('update queryPhoneNumberGroups:', options.phoneNumber);

    if (!options.phoneNumber) {
      this.messagingGroups.next([]);
      return [];
    }

    const response = await lastValueFrom(
      this.apiMessagingService.messagingGetMessagingGroupsV2({
        phoneNumber: options.phoneNumber,
        body: {
          phoneNumber: options.phoneNumber,
          searchText: '',
          skip: 0,
          limit: 100,
          sort: [],
          user: undefined,
        },
      }),
    );
    const allGroups = response.data;
    this.totalGroups = response.data.length;

    await this.updateGroupsBadges(allGroups, this.notificationsService.latestNotifications.getValue());

    return allGroups;
  }

  public async updateGroupsBadges(groups: MessagingGroupModel[], notifications: NotificationModel[]) {
    console.log('updateGroupsBadges: ' + notifications.length);
    let hasNotifications = false;
    for (const group of groups) {
      const notification = notifications.find(
        x => x.eventType === 'sms' && x.tnTo === this.getMessagingNumber() && x.thread === group.threadKey,
      );

      if (notification) {
        hasNotifications = true;
        group.unread = notification.badge;
      } else {
        group.unread = 0;
      }
    }

    if (hasNotifications) {
      const messagingGroups = this.messagingGroups.getValue();
      this.messagingGroups.next(messagingGroups);
    }
  }

  public updatePhoneNumbersBadges(notifications: NotificationModel[]) {
    for (const number of this.messagingNumbers) {
      const phoneNotifications = notifications.filter(x => x.eventType === 'sms' && x.tnTo === number.phoneNumber);
      let totalBadges = 0;
      for (const notification of phoneNotifications) {
        totalBadges += notification.badge;
      }
      number.badge = totalBadges;
    }
  }

  public async sendMessage(request: SendMessageModel): Promise<MessageModel> {
    const response = await lastValueFrom(this.apiMessagingService.messagingSendMessage({ body: request }));

    if (response) {
      console.log(response.messageId);
      return response;
    }

    return undefined;
  }

  public async getMessagingLimits(): Promise<BillingCycleLimitModel> {
    const response = await lastValueFrom(this.apiMessagingService.messagingGetMessagingLimits());
    return response.data;
  }

  public async deleteMessageBodyById(messageId): Promise<MessageModel> {
    return await lastValueFrom(this.apiMessagingService.messagingDeleteMessageBody({ messageId: messageId }));
  }

  public async deleteFullConversation(threadKey: string, block: boolean): Promise<boolean> {
    if (!!threadKey) {
      return await lastValueFrom(
        this.apiMessagingService.messagingDeleteFullConversationByGroup({ groupKey: threadKey, block: block }),
      );
    }

    return false;
  }

  public async markUnreadMessages(group: MessagingGroupModel): Promise<void> {
    if (!this.getMessagingNumber()) {
      return;
    }

    await this.notificationsService.markUnreadNotifications(0, this.getMessagingNumber() + '-' + group.threadKey);

    const messagingGroups = this.messagingGroups.getValue();
    this.messagingGroups.next(messagingGroups);
  }

  public async markUnreadMessagesByThreadKey(threadKey: string): Promise<void> {
    if (!this.getMessagingNumber()) {
      return;
    }

    await this.notificationsService.markUnreadNotifications(0, this.getMessagingNumber() + '-' + threadKey);

    const messagingGroups = this.messagingGroups.getValue();
    this.messagingGroups.next(messagingGroups);
  }

  public async markMessageAsUnreadByThreadKey(threadKey: string): Promise<void> {
    if (!this.getMessagingNumber()) {
      return;
    }

    await this.notificationsService.markMessageAsUnread(1, this.getMessagingNumber() + '-' + threadKey);

    const messagingGroups = this.messagingGroups.getValue();
    this.messagingGroups.next(messagingGroups);
  }

  public async messagingPhoneNumbers(tn?: string, force?: boolean): Promise<SmsPhoneNumberModel[]> {
    console.log('messagingPhoneNumbers:', tn);
    this.getNumbersInProgress = true;
    if (!this.messagingNumbers || this.messagingNumbers.length === 0) {
      this.messagingNumbers = await lastValueFrom(this.apiMessagingService.messagingGetMyMessagingPhoneNumbers());
    }
    this.getNumbersInProgress = false;

    this.updatePhoneNumbersBadges(this.notificationsService.latestNotifications.getValue());

    if (force || (!this.getMessagingNumber() && this.messagingNumbers.length > 0)) {
      this.selectedMessagingNumber = this.messagingNumbers.find(x => x.phoneNumber === tn);
      if (!this.selectedMessagingNumber) {
        this.selectedMessagingNumber = this.messagingNumbers.find(x => x.badge > 0) || this.messagingNumbers[0];
      }
    }

    return this.messagingNumbers;
  }

  public getContactsAndTagsObservable(searchText?: string) {
    return this.apiMessagingService.messagingLookupContactsAndTags({ q: searchText });
  }
}
